import {
  DocumentComposer,
  FileCard,
  NoteCard,
  PhotoCard,
  useCustomerDocuments,
} from "@mytour/documents";
import type {DocumentParentType, FileDocumentResponse} from "@mytour/shared";
import {Loading} from "@mytour/ui/Loading";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import {useCertificateDocuments} from "@mytour/chimney-sweeping";
import {useMemo, useState} from "react";
import {useJardinierReportDocumentsByCustomer} from "../patient/report/jardinier/query/jardinierReportQueries";
import {
  type DocumentFeedItem,
  type FeedDayGroup,
  type FeedEntry,
  groupDocumentsForFeed,
} from "./documentGrouping";
import {AttestationCard} from "./file-render/AttestationCard";
import {InvoiceCard} from "./file-render/InvoiceCard";
import {JardinierReportCard} from "./file-render/JardinierReportCard";
import {useInvoiceDocumentsByCustomer} from "./query/invoiceDocumentQueries";

dayjs.locale("fr");

type Props = {
  parentType: DocumentParentType;
  parentId: string;
  onComposingChange?: (composing: boolean) => void;
  footerSlot?: HTMLElement | null;
};

export function DocumentsTimeline({parentType, parentId, onComposingChange, footerSlot}: Props) {
  const documents = useDocumentsForParent(parentType, parentId);
  const [isComposing, setIsComposing] = useState(false);
  const [resumeDocument, setResumeDocument] = useState<FileDocumentResponse>();
  const groups = useMemo(
    () => (documents.list ? groupDocumentsForFeed(documents.list) : []),
    [documents.list]
  );

  // Les attestations sont toujours rattachées à un client — l'id n'est exploitable ici que
  // pour ce parentType (cf. `useDocumentsForParent` ci-dessous, pas encore câblé pour ServiceTarget).
  const customerId = parentType === "CUSTOMER" ? parentId : undefined;

  return (
    <div>
      <DocumentComposer
        resumeDocument={resumeDocument}
        parentType={parentType}
        parentId={parentId}
        onActiveChange={handleActiveChange}
        footerSlot={footerSlot}
      />
      <div
        style={{
          display: "grid",
          gridTemplateRows: isComposing ? "0fr" : "1fr",
          opacity: isComposing ? 0 : 1,
          pointerEvents: isComposing ? "none" : "auto",
          transition: "grid-template-rows 180ms ease-out, opacity 180ms ease-out",
        }}
        aria-hidden={isComposing}
      >
        <div style={{minHeight: 0, overflow: "hidden"}}>
          <div style={{maxHeight: "60vh", overflowY: "auto", position: "relative"}}>
            <div
              style={{
                position: "sticky",
                top: 0,
                zIndex: 1,
                height: 0,
                boxShadow: "0px 0px 11px 10px rgba(255, 255, 255, 1)",
              }}
              aria-hidden
            />
            {documents.isLoading ? (
              <Loading />
            ) : groups.length === 0 ? (
              <div className="text-muted text-center py-4">Aucun document pour le moment.</div>
            ) : (
              <div className="d-flex flex-column gap-3">
                {groups.map((group) => (
                  <DayGroup
                    key={group.dayKey}
                    group={group}
                    customerId={customerId}
                    onResume={handleResumeDocument}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  function handleResumeDocument(document: FileDocumentResponse) {
    setResumeDocument({...document});
  }

  function handleActiveChange(active: boolean) {
    setIsComposing(active);
    onComposingChange?.(active);
  }
}

function DayGroup({
  group,
  customerId,
  onResume,
}: {
  group: FeedDayGroup;
  customerId: string | undefined;
  onResume: (document: FileDocumentResponse) => void;
}) {
  const day = dayjs(group.date);
  return (
    <div>
      <div
        style={{
          padding: "8px 0 2px 0",
          marginTop: "8px",
          borderBottom: "2px solid rgba(var(--mt-color-action-rgb), 0.21)",
        }}
      >
        <span style={{fontWeight: "bold", fontSize: "15px", color: "var(--mt-color-action)"}}>
          {day.format("dddd D MMMM YYYY")}
        </span>
      </div>
      <div className="flex-grow-1 d-flex gap-2 flex-wrap">
        {group.entries.map((entry, i) => (
          <EntryRenderer
            key={entryKey(entry, i)}
            entry={entry}
            customerId={customerId}
            onResume={onResume}
          />
        ))}
      </div>
    </div>
  );
}

function EntryRenderer({
  entry,
  customerId,
  onResume,
}: {
  entry: FeedEntry;
  customerId: string | undefined;
  onResume: (document: FileDocumentResponse) => void;
}) {
  if (entry.kind === "invoice") {
    return <InvoiceCard doc={entry.document} />;
  }
  if (entry.kind === "note") {
    return <NoteCard doc={entry.document} />;
  }
  if (entry.kind === "file") {
    return <FileCard doc={entry.document} onResume={onResume} />;
  }
  if (entry.kind === "attestation") {
    // customerId non-undefined : une attestation n'existe que sous parentType CUSTOMER (cf. ci-dessus).
    return <AttestationCard doc={entry.document} customerId={customerId as string} />;
  }
  if (entry.kind === "jardinier") {
    // customerId non-undefined : un compte rendu n'existe que sous parentType CUSTOMER (cf. ci-dessus).
    return <JardinierReportCard doc={entry.document} customerId={customerId as string} />;
  }
  return <PhotoCard docs={entry.documents} onResume={onResume} />;
}

export function entryKey(entry: FeedEntry, fallback: number): string {
  if (entry.kind === "invoice") {
    return `invoice:${entry.document.id}`;
  }
  if (entry.kind === "photos") {
    return entry.documents.map((d) => d.id).join("|");
  }
  if (entry.kind === "attestation" || entry.kind === "jardinier") {
    // Index unique côté ReportFile = (eventId, type) : un même event peut porter à la fois
    // une attestation et un compte rendu, d'où le préfixe par kind.
    return `${entry.kind}:${entry.document.eventId}`;
  }
  return entry.document.id ?? `idx-${fallback}`;
}

function useDocumentsForParent(parentType: DocumentParentType, parentId: string) {
  const isCustomerParent = parentType === "CUSTOMER";
  // ServiceTarget n'est pas encore câblé (#341) — les trois hooks restent désactivés pour lui.
  const invoices = useInvoiceDocumentsByCustomer(parentId, isCustomerParent);
  const customer = useCustomerDocuments(parentId, isCustomerParent);
  const attestations = useCertificateDocuments(parentId, isCustomerParent);
  const jardinierReports = useJardinierReportDocumentsByCustomer(parentId, isCustomerParent);

  const list = useMemo<DocumentFeedItem[] | undefined>(() => {
    if (
      !invoices.invoiceDocuments &&
      !customer.documents &&
      !attestations.certificateDocuments &&
      !jardinierReports.jardinierReportDocuments
    ) {
      return undefined;
    }
    const uploadItems: DocumentFeedItem[] = (customer.documents ?? []).map((document) => ({
      source: "upload",
      document,
    }));
    const attestationItems: DocumentFeedItem[] = (attestations.certificateDocuments ?? []).map(
      (document) => ({source: "attestation", document})
    );
    const jardinierItems: DocumentFeedItem[] = (
      jardinierReports.jardinierReportDocuments ?? []
    ).map((document) => ({source: "jardinier", document}));
    const invoiceItems: DocumentFeedItem[] = (invoices.invoiceDocuments ?? []).map((document) => ({
      source: "invoice",
      document,
    }));
    return [...uploadItems, ...attestationItems, ...jardinierItems, ...invoiceItems];
  }, [
    invoices.invoiceDocuments,
    customer.documents,
    attestations.certificateDocuments,
    jardinierReports.jardinierReportDocuments,
  ]);

  return {
    list,
    isLoading:
      invoices.isInvoiceDocumentsLoading ||
      customer.isDocumentsLoading ||
      attestations.isCertificateDocumentsLoading ||
      jardinierReports.isJardinierReportDocumentsLoading,
  };
}
